import type { Ssm5DtcReadout } from '@packages/domain/ssm5';

import { useAbortController } from '@packages/core/http';
import { getLogger } from '@packages/core/logging';
import { GetAllDtcs } from '@packages/domain/ssm5';
import { container } from '@web/services/container';
import { useCallback, useEffect, useRef, useState } from 'react';

const [$getAllDtcs] = container.resolveOperations(GetAllDtcs);

const logger = getLogger('ssm5');

/** The DTC read of a session, and the way to read again after a recoverable failure. */
export type Ssm5Dtcs = {
    /** Read the DTCs again, whatever the last read found; does nothing while a read is in flight or without a session. */
    reload: () => void;
    /** Read the DTCs again; does nothing unless the last read failed in a way reading again can recover from. */
    retry: () => void;
    /** Where the read stands. */
    state: Ssm5DtcsState;
};

/** Where the DTC read of a session stands. */
export type Ssm5DtcsState =
    | {
          /** Marks a read that failed. */
          status: 'failed';
          /** Whether reading again can succeed within the same session. */
          isRecoverable: boolean;
      }
    | {
          /** Marks the absence of a session to read. */
          status: 'idle';
      }
    | {
          /** Marks a completed read. */
          status: 'loaded';
          /** What the read found. */
          readout: Ssm5DtcReadout;
      }
    | {
          /** Marks a read in flight. */
          status: 'loading';
          /** How far the read is, from 0 to 100, as the backend reports it. */
          progress: number;
      };

/** A read that has settled. */
type SettledRead = Extract<Ssm5DtcsState, Record<'status', 'failed' | 'loaded'>>;

const IDLE: Ssm5DtcsState = { status: 'idle' };

/**
 * Read the DTCs of the session behind a Cloud Connector ID: once per ID, again on a retry after a
 * recoverable failure, aborted when the ID changes or the component unmounts. Each read replaces what
 * the previous one found, so nothing is shown twice and nothing stale outlives a failure.
 *
 * @param cloudConnectorId - the Cloud Connector ID of the session, or undefined without one
 * @returns where the read stands, the reload and the retry
 */
export function useSsm5Dtcs(cloudConnectorId: string | undefined): Ssm5Dtcs {
    const { abortSignalRef, signalAbort } = useAbortController();
    const [state, setState] = useState<Ssm5DtcsState>(IDLE);
    const [readCount, setReadCount] = useState(0);
    // Whether the settled read allows a retry. It lives in a ref and is cleared by the retry it allows, so
    // presses that land before the next render cannot start a second read.
    const canRetryRef = useRef(false);
    const isReadingRef = useRef(false);

    useEffect(() => {
        canRetryRef.current = false;
        isReadingRef.current = cloudConnectorId !== undefined;

        if (cloudConnectorId === undefined) {
            setState(IDLE);

            return;
        }

        const signal = abortSignalRef.current;

        setState({ progress: 0, status: 'loading' });
        readDtcs(cloudConnectorId, signal, (progress) => {
            if (!signal.aborted) {
                setState({ progress, status: 'loading' });
            }
        }).then((settled) => {
            if (!signal.aborted) {
                isReadingRef.current = false;
                canRetryRef.current = settled.status === 'failed' && settled.isRecoverable;
                setState(settled);
            }
        });

        return signalAbort;
    }, [abortSignalRef, cloudConnectorId, readCount, signalAbort]);

    const retry = useCallback(() => {
        if (canRetryRef.current) {
            canRetryRef.current = false;
            isReadingRef.current = true;
            setReadCount((count) => count + 1);
        }
    }, []);

    const reload = useCallback(() => {
        if (cloudConnectorId === undefined) {
            return;
        }

        if (isReadingRef.current) {
            return;
        }

        canRetryRef.current = false;
        isReadingRef.current = true;
        setReadCount((count) => count + 1);
    }, [cloudConnectorId]);

    return { reload, retry, state };
}

/**
 * Read the DTCs of a session. A read that throws, aborted or not, reads as a recoverable failure; a
 * caller that aborted ignores the answer anyway. A throw that no abort caused is logged, as the connect
 * flow does, since nothing else reports it.
 *
 * @param cloudConnectorId - the Cloud Connector ID of the session
 * @param signal - aborts the read
 * @param onProgress - receives the progress of the read as the backend reports it
 * @returns how the read settled
 */
async function readDtcs(
    cloudConnectorId: string,
    signal: AbortSignal,
    onProgress: (progress: number) => void
): Promise<SettledRead> {
    try {
        const result = await $getAllDtcs({ cloudConnectorId, onProgress, signal });

        return result.success
            ? { readout: result.readout, status: 'loaded' }
            : { isRecoverable: result.isRecoverable, status: 'failed' };
    } catch (error: unknown) {
        if (!signal.aborted) {
            logger.error(`The SSM5 DTC read through [${cloudConnectorId}] failed before it settled`, {
                cloudConnectorId,
                error,
            });
        }

        return { isRecoverable: true, status: 'failed' };
    }
}
